import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAdminNewsDetail } from '../../Reducers/actions/newsActions';
import { AdminDetailPageSkeleton } from '../../components/Skeletons/BatchScreenSkeletons';
import Icon from '../../components/Icon/Icon';
import KPICard from '../../components/Dashboard/KPICard/KPICard';
import { calculateReadingTime } from '../../../helpers/utilities';
import { formatEurFromCents } from '../../../helpers/money';
import moment from 'moment';
import './AdminNewsDetail.scss';
import './Admin.scss';

function formatReadTime(data) {
    if (data?.read_time != null) {
        const mins = typeof data.read_time === 'number' ? data.read_time : parseInt(data.read_time, 10);
        return Number.isNaN(mins) ? '—' : `${mins}mins`;
    }
    if (data?.content) {
        const mins = calculateReadingTime(data.content);
        return `${mins}mins`;
    }
    return '—';
}

const ENGAGEMENT_ITEMS = [
    { key: 'total_likes', apiKey: 'total_likes', label: 'Liked', icon: 'engagement-liked' },
    { key: 'views_count', apiKey: 'views_count', label: 'Views', icon: 'engagement-views' },
    { key: 'total_comments', apiKey: 'total_comments', label: 'Comments', icon: 'comment' },
    { key: 'featured_count', apiKey: 'featured_count', label: 'Favoured', icon: 'star' },
    { key: 'shares_count', apiKey: 'shares_count', label: 'Shares', icon: 'share' },
];

function AdminNewsDetail() {
    const { news_id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!news_id) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetchAdminNewsDetail(news_id);
                if (!cancelled && response?.success && response?.data) {
                    setData(response.data);
                } else if (!cancelled) {
                    setError('News not found');
                }
            } catch (err) {
                if (!cancelled) {
                    setError('Failed to load news details');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [news_id]);

    if (loading) {
        return (
            <AdminDetailPageSkeleton className="admin-news-detail" />
        );
    }

    if (error || !data) {
        return (
            <div className="admin-news-detail">
                <div className="admin-news-detail-error">
                    <Icon alias="alert" color="#FF3232" width={64} />
                    <h2>{error || 'News not found'}</h2>
                    <button type="button" className="back-btn" onClick={() => navigate('/admin/news')}>
                        Back to News
                    </button>
                </div>
            </div>
        );
    }

    const title = data.title || data.news_title || '—';
    const creatorId = data.creator_id || data.creator?.user_id || data.users?.creator_id;
    const creatorUsername = data.creator_username ?? data.creator?.userName ?? data.users?.username ?? '';
    const displayUsername = creatorUsername.startsWith('@') ? creatorUsername : `@${creatorUsername}`;
    const status = data.status || 'active';
    const readTime = formatReadTime(data);
    const oneTimeContributors = data.one_time_contributors || data.oneTimeContributors || [];
    const monthlyContributors = data.monthly_contributors || data.monthlyContributors || [];
    const totalOneTime = data.total_one_time_contributions_count ?? data.totalOneTimeContributions ?? oneTimeContributors.length;
    const totalMonthly = data.total_monthly_contributions_count ?? data.totalMonthlyContributions ?? monthlyContributors.length;
    const oneTimeAmount = data.one_time_contributions_amount ?? data.oneTimeContributionsAmount ?? oneTimeContributors.reduce((s, c) => s + (c.amount || 0), 0);
    const monthlyAmount = data.monthly_contributions_amount ?? data.monthlyContributionsAmount ?? monthlyContributors.reduce((s, c) => s + (c.amount || 0), 0);

    return (
        <div className="admin-news-detail">
            <div className="news-detail-header">
                <div className="breadcrumb">
                    <button type="button" className="breadcrumb-link" onClick={() => navigate('/admin/news')}>
                        News {data.news_total_count != null ? `[${Number(data.news_total_count).toLocaleString()}]` : ''}
                    </button>
                    <span className="breadcrumb-separator">
                        <Icon alias="chevron-right" width={14} height={14} color="#666" />
                    </span>
                    <span className="breadcrumb-current">News details</span>
                </div>
                <div className="news-detail-header-right">
                    <div className={`status-tag status-${(status || 'active').toLowerCase()}`}>
                        <span>{status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Active'}</span>
                        <Icon alias="chevron-down" width={14} height={14} color="currentColor" />
                    </div>
                </div>
            </div>

            <div className="news-detail-grid">
                <div className="news-summary-card">
                    <div className="news-summary-left">
                        <h1 className="news-detail-title">{title}</h1>
                        <p className="news-detail-creator">
                            by{' '}
                            {creatorId ? (
                                <button
                                    type="button"
                                    className="creator-link"
                                    onClick={() => navigate(`/admin/users/${creatorId}`)}
                                >
                                    {displayUsername}
                                </button>
                            ) : (
                                displayUsername || '—'
                            )}
                        </p>
                        {(oneTimeAmount > 0 || monthlyAmount > 0) && (
                            <span className="monetised-pill">
                                Monetised
                                <Icon alias="chevron-right" width={12} height={12} color="currentColor" />
                            </span>
                        )}
                    </div>
                    <dl className="news-meta news-summary-right">
                        <div className="meta-row">
                            <dt>Read time</dt>
                            <dd>{readTime}</dd>
                        </div>
                        <div className="meta-row">
                            <dt>Created at</dt>
                            <dd>{data.created_at ? moment(data.created_at).format('DD/MM/YYYY HH:mm') : '—'}</dd>
                        </div>
                        <div className="meta-row">
                            <dt>Updated at</dt>
                            <dd>{data.updated_at ? moment(data.updated_at).format('DD/MM/YYYY HH:mm') : '—'}</dd>
                        </div>
                    </dl>
                </div>

                <div className="engagement-card">
                    <div className="engagement-card-header">
                        <button
                            type="button"
                            className="view-news-btn"
                            onClick={() => window.open(`/news/${news_id}`, '_blank')}
                            aria-label="View news"
                        >
                            VIEW NEWS
                            <Icon alias="arrow-right" width={14} height={14} color="#FF3232" />
                        </button>
                    </div>
                    <div className="engagement-metrics">
                        {ENGAGEMENT_ITEMS.map(({ key, apiKey, label, icon }) => {
                            const rawValue = data[apiKey] ?? data[key] ?? 0;
                            const value = typeof rawValue === 'number'
                                ? rawValue.toLocaleString()
                                : String(rawValue);
                            return (
                                <div key={key} className="engagement-item">
                                    <Icon alias={icon} width={40} height={40} stroke="#FF8CDC" strokeWidth={1} color="#FF8CDC" />
                                    <span className="engagement-value">{value}</span>
                                    <span className="engagement-label">{label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="contributions-summary-row">
                <KPICard
                    title="Total One-time contributions"
                    value={totalOneTime}
                    showInfoIcon={false}
                />
                <KPICard
                    title="One-time contributions"
                    value={formatEurFromCents(oneTimeAmount)}
                    showInfoIcon={false}
                />
                <KPICard
                    title="Total Monthly contributions"
                    value={totalMonthly}
                    showInfoIcon={false}
                />
                <KPICard
                    title="Monthly contributions"
                    value={formatEurFromCents(monthlyAmount)}
                    showInfoIcon={false}
                />
            </div>

            <div className="contributions-tables-row">
                <div className="contribution-block">
                    <h3 className="contribution-title">One-time Contributors</h3>
                    <div className="contribution-table-wrapper">
                        <table className="contribution-table">
                            <thead>
                                <tr>
                                    <th>
                                        <span className="th-with-icon">One-time Contributors<Icon alias="arrow-up-down" width={14} height={14} color="#999" /></span>
                                    </th>
                                    <th>
                                        <span className="th-with-icon">Amount<Icon alias="arrow-up-down" width={14} height={14} color="#999" /></span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {oneTimeContributors.length === 0 ? (
                                    <tr>
                                        <td colSpan={2}>No one-time contributions yet</td>
                                    </tr>
                                ) : (
                                    oneTimeContributors.map((row, idx) => (
                                        <tr key={row.user_id || idx}>
                                            <td>
                                                {row.user_id ? (
                                                    <button
                                                        type="button"
                                                        className="table-username-link"
                                                        onClick={() => navigate(`/admin/users/${row.user_id}`)}
                                                    >
                                                        {row.username?.startsWith('@') ? row.username : `@${row.username || ''}`}
                                                    </button>
                                                ) : (
                                                    row.username?.startsWith('@') ? row.username : row.username ? `@${row.username}` : '—'
                                                )}
                                            </td>
                                            <td>{row.amount != null ? formatEurFromCents(row.amount) : '—'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="contribution-block">
                    <h3 className="contribution-title">Monthly Contributors</h3>
                    <div className="contribution-table-wrapper">
                        <table className="contribution-table">
                            <thead>
                                <tr>
                                    <th>
                                        <span className="th-with-icon">Monthly Contributors<Icon alias="arrow-up-down" width={14} height={14} color="#999" /></span>
                                    </th>
                                    <th>
                                        <span className="th-with-icon">Amount<Icon alias="arrow-up-down" width={14} height={14} color="#999" /></span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyContributors.length === 0 ? (
                                    <tr>
                                        <td colSpan={2}>No monthly contributions... check again later.</td>
                                    </tr>
                                ) : (
                                    monthlyContributors.map((row, idx) => (
                                        <tr key={row.user_id || idx}>
                                            <td>
                                                {row.user_id ? (
                                                    <button
                                                        type="button"
                                                        className="table-username-link"
                                                        onClick={() => navigate(`/admin/users/${row.user_id}`)}
                                                    >
                                                        {row.username?.startsWith('@') ? row.username : `@${row.username || ''}`}
                                                    </button>
                                                ) : (
                                                    row.username?.startsWith('@') ? row.username : row.username ? `@${row.username}` : '—'
                                                )}
                                            </td>
                                            <td>{row.amount != null ? formatEurFromCents(row.amount) : '—'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminNewsDetail;
